import React, { useEffect, useState, useRef } from "react";

import styles from "./InputNumber.module.css";

import { clamp } from "@/utils/dateUtils";

import Button from "../Button/Button";
import TextInput from "../TextInput/TextInput";
import IconMinus from "../../icons/IconMinus";
import IconPlus from "../../icons/IconPlus";

const NUM_REGEX = "^(?:[\\-+]?\\d*(\\d\\.|\\.\\d)?\\d*)?$";
interface InputNumberProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"|"prefix"> {
  className?: string;
  inputId?: string;
  value?: number;
  min?: number;
  max?: number;
  showControls?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  centerText?: boolean;
  isReadonly?: boolean;
  wrapNumber?: boolean;
  tabIndex?: number;
  onChange?: (value: number | null) => void;
}

export default function InputNumber(props: InputNumberProps) {
  const {
    onChange,
    inputId,
    className,
    value,
    min = 0,
    max = Number.MAX_SAFE_INTEGER,
    showControls = true,
    prefix = null,
    suffix = null,
    invalid = false,
    disabled = false,
    centerText = true,
    isReadonly = false,
    wrapNumber = false,
    placeholder,
    tabIndex = undefined,
    ...rest
  } = props;
  const [internalValue, setInternalValue] = useState(value?.toString() ?? "");
  const [hasFocus, setHasFocus] = useState(false);
  const [selectionRange, setSelectionRange] = useState<[number, number, "forward" | "backward" | "none"]>(null);
  const timer = useRef<NodeJS.Timer>(null);
  const ref = useRef<HTMLInputElement>(null);

  const stopTimer = () => {
    clearTimeout(timer.current);
    clearInterval(timer.current);
  };

  const updateSelectionRange = () => setSelectionRange(
    ref.current
      ? [
          ref.current.selectionStart,
          ref.current.selectionEnd,
          ref.current.selectionDirection,
        ]
      : null
  );

  useEffect(updateSelectionRange, [ref.current]);

  useEffect(() => {
    if (hasFocus && value != null && clamp(parseFloat(internalValue.match(NUM_REGEX)?.[0]), min, max) !== value) {
      // sometimes, during editing, the string of the parsed number may not exactly match the original
      // string; e.g., if the input value is "02" or "-", we wait until blur to clean it up.
      // also if the user is typing a number outside the allowed range, we don't screw up their typing.
      // we report the clamped value to onChange() and then clamp the input value on blur
      setInternalValue(value?.toString() ?? "");
    } else if (!hasFocus && (value?.toString() ?? "") !== internalValue) {
      setInternalValue(value?.toString() ?? "");
    }
  }, [value, min, max, hasFocus]);

  useEffect(() => {
    if(value < min || value > max) {
      onChange?.(clamp(value, min, max));
    }
  });

  const continuous = f => (...args) => {
    stopTimer();
    f(...args, false);
    timer.current = setTimeout(() => {
      timer.current = setInterval(() => f(...args, true), 80);
    }, 300);
  };

  const add = (n: number) => (isRepeatCall: boolean = false) => {
    let parsedValue = parseFloat(ref.current.value);
    let newValue = 0;
    if(!isNaN(parsedValue)) parsedValue = parsedValue + n;
    if(wrapNumber && !isRepeatCall) {
      if(parsedValue < min)      parsedValue = (max + 1) + (parsedValue - min);
      else if(parsedValue > max) parsedValue = (min - 1) + (parsedValue - max);
    }
    newValue = clamp(parsedValue, min, max);
    if (onChange) {
      onChange(newValue);
    } else {
      setInternalValue(newValue.toString());
    }
  };
  const inc = continuous(add(1));
  const dec = continuous(add(-1));

  useEffect(() => {
    window.addEventListener('mouseup', stopTimer);
    return () => {
      window.removeEventListener('mouseup', stopTimer);
      stopTimer();
    };
  }, []);

  const internalOnChange = (inputValue: string) => {
    const matchedValue = inputValue.match(NUM_REGEX)?.[0];
    let parsedValue = clamp(parseFloat(matchedValue), min, max);
    if (isNaN(parsedValue)) parsedValue = null;

    if (matchedValue == null) {
      // reject the change and reset the cursor position
      setInternalValue(internalValue);
      if (selectionRange) requestAnimationFrame(() => ref.current.setSelectionRange(...selectionRange));
    } else {
      setInternalValue(inputValue);
      if (parsedValue != null) {
        // we do not report a change to null until blur.
        // this is because while the user is typing they might e.g. delete the number and then type another.
        // we don't want to signal a potentially invalid state in this case.
        onChange?.(parsedValue);
      }
      updateSelectionRange();
    }


    return parsedValue;
  };

  const onBlur = () => {
    setHasFocus(false);
    const parsedValue = internalOnChange(internalValue);
    if (parsedValue?.toString() !== internalValue) {
      setInternalValue(parsedValue?.toString() ?? "");
    }
    if (parsedValue == null) {
      onChange?.(parsedValue);
    }
  }

  return (
    <div
      className={`${className ?? ''} ${styles.container} ${invalid ? styles.invalid : ''} ${disabled ? styles.disabled : ''}`} {...rest}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => { e.preventDefault(); e.stopPropagation(); ref.current.focus() }}
    >
      {showControls && (
        <Button
          className={styles.iconButton}
          type="text"
          disabled={disabled}
          onMouseDown={e => { e.stopPropagation(); dec() }}
        >
          <IconMinus size={16} />
        </Button>
      )}
      <TextInput
        ref={ref}
        inputId={inputId}
        type="text"
        centerText={centerText}
        value={internalValue}
        disabled={disabled}
        prefix={prefix}
        suffix={suffix}
        min={min}
        max={max}
        placeholder={placeholder}
        isReadonly={isReadonly}
        onChange={internalOnChange}
        onKeyDown={updateSelectionRange}
        onMouseDown={e => { e.stopPropagation(); updateSelectionRange() }}
        onSelectionChange={updateSelectionRange}
        onFocus={() => setHasFocus(true)}
        onBlur={onBlur}
        tabIndex={tabIndex}
        borderless
      />
      {showControls && (
        <Button
          className={styles.iconButton}
          type="text"
          disabled={disabled}
          onMouseDown={e => { e.stopPropagation(); inc() }}
        >
          <IconPlus size={16} />
        </Button>
      )}
    </div>
  );
}
